/**
 * PreviewCard — right-rail "How it looks in the library" preview.
 * Mirrors `.preview-card` from the prototype
 * (cms-redesign-mockup.html ~8702–8738 + CSS lines 5015–5201).
 *
 * Renders a live `.lib-tile`:
 *   • Background image — `session_image` (full preview or uploaded
 *     blob URL when the user just swapped it)
 *   • Free pill (top-left) — when `session_is_free`
 *   • New ribbon (top-right) — when `session_is_new`
 *   • Crumb — parent name (Category / Subcategory / Series / Challenge
 *     / Audiobook) resolved from `associatedParent`
 *   • Title — `session_name`
 *   • Meta — author name + duration
 *   • Play button — teal circle bottom-right (decorative)
 *
 * Footer surfaces the canonical web URL with a click-to-open link.
 * The path mirrors the WebApp's `/tapping/{id}` route.
 *
 * Resolves the parent and author *names* via Redux selectors that
 * are already populated by the parent form's `loadData()` effect,
 * so this component stays read-only / side-effect-free.
 */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import { WebAppSessionPlayIcon } from '../../redesign/edit/webappPreviewIcons';

// Suffix used by ParentSelectButton (and v1) to encode the parent
// type inside `associatedParent`. Audiobooks are categories with
// the `category_is_audiobook` flag, so AUD shares the categories
// lookup — we just don't filter it out.
function parseAssociatedParent(value) {
  if (!value || value === '0') return null;
  const m = String(value).match(/^(\d+)(CAT|SUB|CHA|SER|AUD)$/);
  if (!m) return null;
  return { id: Number(m[1]), suffix: m[2] };
}

// Normalize a Redux slice that the codebase exposes inconsistently as
// either an array, an object map (keyed by id), or null.
function toArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.values(raw);
}

export default function PreviewCard({ formData }) {
  const authors = useSelector((s) => s.authors?.authors) || [];
  const rawCategories = useSelector((s) => s.categories?.all_categories);
  const rawSubcategories = useSelector((s) => s.subcategories?.subcategories);
  const rawChallenges = useSelector((s) => s.challenges?.challenges);
  const rawSeries = useSelector((s) => s.series?.all_series);

  const parentName = useMemo(() => {
    const parsed = parseAssociatedParent(formData.associatedParent);
    if (!parsed) return null;
    const lookups = {
      CAT: { list: toArray(rawCategories), idKey: 'category_id', nameKey: 'category_title' },
      AUD: { list: toArray(rawCategories), idKey: 'category_id', nameKey: 'category_title' },
      SUB: { list: toArray(rawSubcategories), idKey: 'subcategory_id', nameKey: 'subcategory_title' },
      CHA: { list: toArray(rawChallenges), idKey: 'challenge_id', nameKey: 'challenge_title' },
      SER: { list: toArray(rawSeries), idKey: 'series_id', nameKey: 'series_title' },
    };
    const cfg = lookups[parsed.suffix];
    if (!cfg) return null;
    const hit = cfg.list.find((o) => Number(o[cfg.idKey]) === parsed.id);
    return hit ? hit[cfg.nameKey] : null;
  }, [formData.associatedParent, rawCategories, rawSubcategories, rawChallenges, rawSeries]);

  const authorName = useMemo(() => {
    if (!formData.session_author_id) return null;
    const hit = authors.find(
      (a) => Number(a.author_id) === Number(formData.session_author_id),
    );
    return hit?.author_name || null;
  }, [formData.session_author_id, authors]);

  const isFree = !!formData.session_is_free;
  const isNew = !!formData.session_is_new;
  const imageUrl = formData.session_image_preview || formData.session_image || '';
  const title = formData.session_name || 'Untitled session';
  const lengthLabel = formData.session_length || '';

  // Prefer the backend-issued `universal_link` (Branch.io URL stored
  // on the session row). That's the canonical share URL — it routes
  // through the deep-link gateway and falls back to the web player on
  // desktop. Strip the scheme for the visible label so it reads as a
  // compact path rather than a noisy `https://…`. New (unsaved)
  // sessions don't have a link yet — show an em-dash placeholder.
  const universalLink = formData.universal_link || null;
  const previewHref = universalLink;
  const previewPath = universalLink
    ? universalLink.replace(/^https?:\/\//, '')
    : '— not yet generated —';

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the library"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      {/* preview-stage — gradient bg + centered tile */}
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 240,
            aspectRatio: '216 / 268',
            borderRadius: 14,
            overflow: 'hidden',
            position: 'relative',
            backgroundImage: imageUrl ? `url("${imageUrl}")` : 'none',
            backgroundColor: imageUrl ? 'transparent' : '#2C3E50',
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            boxShadow: '0 8px 20px #2C3E5025, 0 1px 0 #FFFFFF80 inset',
          }}
        >
          {/* dark gradient overlay for text legibility */}
          <span
            aria-hidden
            style={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, #00000000 30%, #00000080 65%, #000000C8 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* FREE pill — top-left */}
          {isFree && (
            <span
              className="font-condensed font-bold uppercase"
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 2,
                fontSize: 11,
                letterSpacing: '0.08em',
                color: '#C4715B', /* coral-d */
                background: '#FFFCF8',
                padding: '4px 11px',
                borderRadius: 999,
                boxShadow: '0 2px 6px #00000028',
              }}
            >
              Free
            </span>
          )}

          {/* NEW corner ribbon — top-right (angled) */}
          {isNew && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 70,
                height: 70,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  position: 'absolute',
                  top: 11,
                  right: -26,
                  transform: 'rotate(45deg)',
                  background: 'linear-gradient(135deg, #FFC107, #F5A623)',
                  color: '#2C3E50',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  padding: '4px 28px',
                  boxShadow: '0 2px 6px #00000028',
                }}
              >
                New
              </span>
            </span>
          )}

          {/* Body — eyebrow + title + author/duration */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '14px 14px 16px',
              zIndex: 1,
              color: '#FFFFFF',
            }}
          >
            {parentName && (
              <div
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 9.5,
                  letterSpacing: '0.12em',
                  color: '#FFFFFFCC',
                  lineHeight: 1.3,
                  marginBottom: 6,
                  textShadow: '0 1px 3px #00000060',
                }}
              >
                {parentName}
              </div>
            )}
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: '-0.005em',
                color: '#FFFFFF',
                textShadow: '0 1px 4px #00000060',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginBottom: 8,
              }}
            >
              {title}
            </div>
            {(authorName || lengthLabel) && (
              <div
                className="flex items-center"
                style={{
                  gap: 8,
                  fontSize: 11.5,
                  color: '#FFFFFFCC',
                  lineHeight: 1.2,
                  textShadow: '0 1px 3px #00000060',
                }}
              >
                {authorName && (
                  <span style={{ fontWeight: 600, color: '#FFFFFF' }}>{authorName}</span>
                )}
                {authorName && lengthLabel && (
                  <span
                    aria-hidden
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      background: '#FFFFFF80',
                      flexShrink: 0,
                    }}
                  />
                )}
                {lengthLabel && <span>{lengthLabel} min</span>}
              </div>
            )}
          </div>

          {/* Play button — bottom-right teal circle */}
          {/* WebApp play icon. Locked variant shown when the session
              is premium (matches what free users see in the app). */}
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              zIndex: 3,
            }}
          >
            <WebAppSessionPlayIcon size={36} isLocked={!formData.session_is_free} />
          </div>
        </div>
      </div>

      {/* preview-foot — URL + Open link */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF', /* sand */
          background: '#FAF4EA', /* cream */
          fontSize: 11,
          gap: 8,
        }}
      >
        <span
          className="font-mono text-text-muted"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewPath}
        </span>
        {previewHref && (
          <a
            href={previewHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-condensed font-bold uppercase no-underline"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72', /* teal-d */
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Open ↗
          </a>
        )}
      </div>
    </RailCard>
  );
}
